import { Box, Button, Typography, useMediaQuery, useTheme } from '@mui/material';
import CustomRating from '../CustomRating/CustomRating';
import React, { FC, useContext, useState } from 'react';
import SurveyService from '../../services/SurveyService';
import NegativeFeedback from './NegativeFeedback';
import PositiveFeedback from './PositiveFeedback';
import { useTranslation } from 'react-i18next';
import { LocalTranslationContext } from '../../hooks/useLocalTranslation';
import CssUtils from '../../shared/CssUtils';
import { useIsSurveyPreview } from '../../hooks/useIsSurveyPreview';

interface Props {
  companyLogo: string;
  primaryColor: string;
  secondaryColor: string;
  question: string;
  companyWebsiteUrl: string;
  customFont: string;
  id: string;
  score: number;
  imprintLink: string;
  privacyPolicyLink: string;
  companyName: string;
  platform: string;
  platformUrl: string;
  platformName: string;
  contactEmail: string;
  title?: string;
  outroText?: string;
  introText?: string;
  introTextNegative?: string;
  contactForm?: boolean;
  scale?: {
    highText: string;
    lowText: string;
  };
  feedbackText?: string;
  feedbackTitle?: string;
  feedbackActive?: boolean;
  shouldShowPrivacyNotice?: boolean;
  privacyNoticeText?: string;
  privacyNoticeLink?: string;
}

const Feedback: FC<Props> = (props) => {
  const {
    companyLogo,
    primaryColor,
    secondaryColor,
    question,
    companyWebsiteUrl,
    customFont,
    id,
    score,
    imprintLink,
    privacyPolicyLink,
    companyName,
    platform,
    platformUrl,
    platformName,
    contactEmail,
    title,
    outroText,
    contactForm,
    scale,
    introText,
    introTextNegative,
    feedbackTitle,
    feedbackText,
    feedbackActive,
    shouldShowPrivacyNotice,
    privacyNoticeText,
    privacyNoticeLink,
  } = props;

  const theme = useTheme();
  const effectiveFontFamily = '' + theme.typography.fontFamily;
  const localContext = useContext(LocalTranslationContext);
  const { t: globalT } = useTranslation();
  const t = localContext ? localContext.t : globalT;
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const isPreview = useIsSurveyPreview();

  const [showButton, setShowButton] = useState<boolean>(true);
  const [selectedRating, setSelectedRating] = useState<number | undefined>(undefined);
  const [feedbackType, setFeedbackType] = useState('default');
  const [lastAnswerId, setLastAnswerId] = useState<string>('');
  const [feedback, setFeedback] = useState<string>('');
  const [privacyNoticeChecked, setPrivacyNoticeChecked] = useState<boolean>(false);

  const isNegativeRating = (selectedRating: number | undefined) => {
    if (selectedRating === undefined) {
      return false;
    }
    return selectedRating < score;
  };

  const handleClick = async () => {
    if (selectedRating === undefined) {
      return;
    }
    setFeedbackType(isNegativeRating(selectedRating) ? 'negative' : 'positive');

    try {
      if (isPreview) {
        setLastAnswerId('preview');
      } else {
        const answer = await SurveyService.saveNpsSurveyAnswer(
          id,
          selectedRating,
          contactEmail,
          formatTextToString(feedback),
          companyName,
          { has_accepted_privacy_notice: privacyNoticeChecked }
        );
        setLastAnswerId(answer.ID);
      }
    } catch (error) {
      console.error('Ein Fehler ist beim Speichern der Antwort aufgetreten:', error);
    }

    setShowButton(false);
  };

  const handleRatingClick = (rating: number) => {
    setSelectedRating(rating);
  };

  const formatTextToString = (text: any) => {
    if (!text) return null;

    return text.replace(/(\r\n|\r|\n)/g, ' ');
  };

  return (
    <Box
      className="survey-content survey-content--nps"
      sx={{ ...CssUtils.surveyContent(), fontFamily: effectiveFontFamily }}
    >
      {feedbackType === 'default' && (
        <Box className="survey-question-step survey-question-step--nps">
          <Typography
            className="survey-question-text survey-question-text--nps"
            variant="h4"
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'center',
              color: secondaryColor,
              mb: theme.spacing(6),
              textAlign: isSmallScreen ? 'center' : 'left',
              fontWeight: isSmallScreen ? 'normal' : 'bold',
              whiteSpace: 'pre-wrap',
              fontFamily: effectiveFontFamily,
              fontSize: { sm: '34px', xs: '26px' },
            }}
          >
            {question}
          </Typography>
          <CustomRating
            highText={scale?.highText}
            lowText={scale?.lowText}
            min={0}
            max={10}
            font={effectiveFontFamily}
            isSmallScreen={isSmallScreen}
            onRatingClick={handleRatingClick}
            currentRating={selectedRating}
          />
          {showButton && (
            <Box
              className="survey-navigation survey-navigation--nps"
              sx={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'end',
                alignItems: 'center',
                width: '100%',
                mt: theme.spacing(4),
              }}
            >
              <Button
                className="survey-button survey-button--send survey-button--nps-rating"
                variant="contained"
                disabled={selectedRating === undefined}
                sx={{
                  backgroundColor: primaryColor,
                  width: 'fit-content',
                  padding: isSmallScreen ? theme.spacing(1) : theme.spacing(2),
                  marginTop: theme.spacing(4),
                }}
                onClick={() => handleClick()}
              >
                {t('confirm')}
              </Button>
            </Box>
          )}
        </Box>
      )}

      {selectedRating !== undefined && feedbackType === 'negative' && lastAnswerId && (
        <NegativeFeedback
          imprint={imprintLink}
          dataProtection={privacyPolicyLink}
          companyName={companyName}
          secondaryColor={secondaryColor}
          selectedRating={selectedRating}
          companyLogo={companyLogo}
          primaryColor={primaryColor}
          platform={platform}
          contactEmail={contactEmail}
          companyWebsiteUrl={companyWebsiteUrl}
          customFont={effectiveFontFamily}
          title={title}
          outroText={outroText}
          introText={introTextNegative ? introTextNegative : introText}
          contactForm={contactForm}
          id={id}
          onChange={setFeedback}
          onCheckPrivacyNotice={setPrivacyNoticeChecked}
          feedbackText={feedbackText}
          feedbackTitle={feedbackTitle}
          feedbackActive={feedbackActive}
          lastAnswerId={lastAnswerId}
          shouldShowPrivacyNotice={shouldShowPrivacyNotice}
          privacyNoticeText={privacyNoticeText}
          privacyNoticeLink={privacyNoticeLink}
        />
      )}

      {selectedRating !== undefined && feedbackType === 'positive' && (
        <PositiveFeedback
          id={id}
          companyWebsiteUrl={companyWebsiteUrl}
          secondary={secondaryColor}
          companyLogo={companyLogo}
          primary={primaryColor}
          platform={platform}
          platformUrl={platformUrl}
          platformName={platformName}
          customFont={effectiveFontFamily}
          title={title}
          introText={introText}
          outroText={outroText}
          companyName={companyName}
          feedbackActive={feedbackActive}
          shouldShowPrivacyNotice={shouldShowPrivacyNotice}
          privacyNoticeText={privacyNoticeText}
          privacyNoticeLink={privacyNoticeLink}
        />
      )}
    </Box>
  );
};

export default Feedback;
